import { useApolloClient } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { getWorkspaceHalfDayRange, isDefined } from 'app-shared/utils';
import { useToast } from 'app-ui/components';
import { Button, Field, Input, Textarea } from 'app-ui/primitives/input';
import { themeCssVariables } from 'app-ui/theme';
import { type KeyboardEvent, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { SKIP_ERROR_TOAST_CONTEXT } from '@/apollo/constants/SkipErrorToastContext';
import { getUserFriendlyErrorMessage } from '@/apollo/utils/getUserFriendlyErrorMessage';
import { hasGraphqlErrorSubCode } from '@/apollo/utils/hasGraphqlErrorSubCode';
import { InterventionAssignmentPicker } from '@/intervention/components/InterventionAssignmentPicker';
import { InterventionConfirmation } from '@/intervention/components/InterventionConfirmation';
import { InterventionScheduleFields } from '@/intervention/components/InterventionScheduleFields';
import { useCancelIntervention } from '@/intervention/hooks/useCancelIntervention';
import { useCreateIntervention } from '@/intervention/hooks/useCreateIntervention';
import { useDeleteIntervention } from '@/intervention/hooks/useDeleteIntervention';
import { useUpdateIntervention } from '@/intervention/hooks/useUpdateIntervention';
import { type InterventionFormValues } from '@/intervention/types/InterventionFormValues';
import { formatInterventionTimestamp } from '@/intervention/utils/formatInterventionTimestamp';
import { getInterventionFormValues } from '@/intervention/utils/getInterventionFormValues';
import { useInterventionTypes } from '@/intervention-type/hooks/useInterventionTypes';
import { ReferenceSelect } from '@/reference-data/components/ReferenceSelect';
import { useReferenceData } from '@/reference-data/hooks/useReferenceData';
import { useCurrentWorkspace } from '@/session/hooks/useCurrentWorkspace';
import { SettingsTextField } from '@/settings/components/SettingsTextField';
import { useCloseSidePanel } from '@/ui/layout/side-panel/hooks/useCloseSidePanel';
import { isSubmitShortcut } from '@/ui/utilities/hotkey/utils/isSubmitShortcut';
import {
  GetInterventionDocument,
  GetInterventionsDocument,
  GetPlanningBoardDocument,
  type InterventionFieldsFragment,
} from '~/generated/graphql';
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[4]};
`;
const StyledActions = styled.div`
  border-top: 1px solid ${themeCssVariables.border.color.light};
  display: flex;
  flex-wrap: wrap;
  gap: ${themeCssVariables.spacing[2]};
  padding-top: ${themeCssVariables.spacing[3]};
`;
const StyledHint = styled.p`
  color: ${themeCssVariables.font.color.secondary};
  font-size: ${themeCssVariables.font.size.sm};
  line-height: ${themeCssVariables.text.lineHeight.lg};
  margin: 0;
`;
type InterventionFormProps = {
  intervention?: InterventionFieldsFragment;
  isReadOnly: boolean;
};
export const InterventionForm = ({
  intervention,
  isReadOnly,
}: InterventionFormProps) => {
  const { t, i18n } = useLingui();
  const client = useApolloClient();
  const workspace = useCurrentWorkspace();
  const { enqueueToast } = useToast();
  const { closeSidePanel } = useCloseSidePanel();
  const referenceData = useReferenceData();
  const interventionTypes = useInterventionTypes();
  // Cache refreshes must never replace the draft's version or exact schedule.
  const [baseline, setBaseline] = useState(intervention);
  const [settings, setSettings] = useState(() => ({
    timeZone: workspace.timeZone,
    workingDays: workspace.workingDays,
    halfDaySlots: workspace.halfDaySlots,
  }));
  const [confirmationAction, setConfirmationAction] = useState<
    'cancel' | 'delete' | null
  >(null);
  const [isPending, setIsPending] = useState(false);
  const [isConflict, setIsConflict] = useState(false);
  const [hasRefreshError, setHasRefreshError] = useState(false);
  const { createIntervention } = useCreateIntervention();
  const { updateIntervention } = useUpdateIntervention();
  const { cancelIntervention } = useCancelIntervention();
  const { deleteIntervention } = useDeleteIntervention();
  const schema = z.object({
    title: z
      .string()
      .trim()
      .min(1, t`Enter an intervention title.`)
      .max(200, t`Use 200 characters or fewer.`),
    clientName: z
      .string()
      .trim()
      .min(1, t`Enter a client name.`)
      .max(200, t`Use 200 characters or fewer.`),
    interventionTypeId: z.string().min(1, t`Choose an intervention type.`),
    clientPhone: z.string().max(50, t`Use 50 characters or fewer.`),
    address: z.string().max(500, t`Use 500 characters or fewer.`),
    notes: z.string().max(10000, t`Use 10000 characters or fewer.`),
    priority: z.string(),
    status: z.string(),
    estimatedDurationMinutes: z
      .number()
      .int(t`Enter a whole number.`)
      .min(1, t`Use a positive number of minutes.`)
      .nullable(),
    collaboratorIds: z.array(z.string()),
    skillIds: z.array(z.string()),
    startDay: z.string(),
    endDay: z.string(),
    startHalfDay: z.enum(['morning', 'afternoon']),
    endHalfDay: z.enum(['morning', 'afternoon']),
  });
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InterventionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: getInterventionFormValues(baseline, settings),
  });
  const isBusy = isPending || isSubmitting;
  const startDay = useWatch({ control, name: 'startDay' });
  const endDay = useWatch({ control, name: 'endDay' });
  const status = useWatch({ control, name: 'status' });
  const collaboratorIds = useWatch({ control, name: 'collaboratorIds' });
  const skillIds = useWatch({ control, name: 'skillIds' });
  const hasDates = startDay !== '' && endDay !== '';
  const availableCollaborators = referenceData.collaborators.filter(
    (collaborator) =>
      (collaborator.isActive && collaborator.isPlannable) ||
      baseline?.assignments.some(
        (assignment) => assignment.collaboratorId === collaborator.id,
      ),
  );
  const existingCollaborators =
    baseline?.assignments.flatMap((assignment) =>
      isDefined(assignment.collaborator) &&
      !availableCollaborators.some(
        (collaborator) => collaborator.id === assignment.collaboratorId,
      )
        ? [assignment.collaborator]
        : [],
    ) ?? [];
  const availableTypes = interventionTypes.interventionTypes.filter(
    (interventionType) =>
      interventionType.isActive ||
      interventionType.id === baseline?.interventionTypeId,
  );
  const referenceError = referenceData.error ?? interventionTypes.error;
  const isLoadingChoices =
    referenceData.isLoading || interventionTypes.isLoading;
  const isDisabled = isReadOnly || isBusy || hasRefreshError;
  const statusOptions =
    baseline?.status === 'cancelled'
      ? [{ value: 'cancelled', label: t`Cancelled` }]
      : hasDates
        ? [
            { value: 'planned', label: t`Planned` },
            { value: 'inProgress', label: t`In progress` },
            { value: 'done', label: t`Done` },
            { value: 'toReplan', label: t`To replan` },
          ]
        : [{ value: 'toPlan', label: t`To plan` }];
  const handleScheduleChange = () => {
    if (baseline?.status === 'cancelled') return;
    const initial = getInterventionFormValues(baseline, settings);
    const draft = getValues();
    const isScheduleChanged =
      draft.startDay !== initial.startDay ||
      draft.endDay !== initial.endDay ||
      draft.startHalfDay !== initial.startHalfDay ||
      draft.endHalfDay !== initial.endHalfDay;
    setValue(
      'status',
      !isScheduleChanged
        ? initial.status
        : draft.startDay !== '' && draft.endDay !== ''
          ? 'planned'
          : 'toPlan',
    );
  };
  const handleFailure = (error: unknown, preserveConflict = false) => {
    const conflict = hasGraphqlErrorSubCode(
      error,
      'INTERVENTION_VERSION_CONFLICT',
    );
    if (!preserveConflict) setIsConflict(conflict);
    setError('root', {
      message: conflict
        ? t`This intervention was changed by someone else. Reload to see the latest version.`
        : (getUserFriendlyErrorMessage(error) ?? t`An error occurred.`),
    });
  };
  const handleSave = async (values: InterventionFormValues) => {
    if (isReadOnly || isPending || isConflict || hasRefreshError) return;
    const initial = getInterventionFormValues(baseline, settings);
    const isScheduleChanged =
      values.startDay !== initial.startDay ||
      values.endDay !== initial.endDay ||
      values.startHalfDay !== initial.startHalfDay ||
      values.endHalfDay !== initial.endHalfDay;
    let schedule:
      | { startsAt: string | null; endsAt: string | null }
      | undefined;
    if (!isDefined(baseline) || isScheduleChanged) {
      if ((values.startDay === '') !== (values.endDay === '')) {
        setError('root', {
          message: t`Choose both dates, or clear both to leave the intervention to plan.`,
        });
        return;
      }
      try {
        schedule =
          values.startDay === ''
            ? { startsAt: null, endsAt: null }
            : {
                startsAt: getWorkspaceHalfDayRange(
                  values.startDay,
                  values.startHalfDay,
                  settings,
                ).startsAt,
                endsAt: getWorkspaceHalfDayRange(
                  values.endDay,
                  values.endHalfDay,
                  settings,
                ).endsAt,
              };
        if (
          isDefined(schedule.startsAt) &&
          isDefined(schedule.endsAt) &&
          schedule.endsAt <= schedule.startsAt
        ) {
          setError('root', { message: t`The end must be after the start.` });
          return;
        }
      } catch {
        setError('root', {
          message: t`Choose valid dates and half-days in the workspace time zone.`,
        });
        return;
      }
    }
    setIsPending(true);
    const input = {
      title: values.title,
      interventionTypeId: values.interventionTypeId,
      clientName: values.clientName,
      clientPhone: values.clientPhone.trim() || null,
      address: values.address.trim() || null,
      notes: values.notes.trim() || null,
      priority: values.priority,
      status: values.status,
      estimatedDurationMinutes: values.estimatedDurationMinutes,
      collaboratorIds: values.collaboratorIds,
      skillIds: values.skillIds,
      ...schedule,
    };
    const result = isDefined(baseline)
      ? await updateIntervention(baseline.id, {
          ...input,
          version: baseline.version,
        })
      : await createIntervention(input);
    setIsPending(false);
    if (!isDefined(result.intervention)) {
      handleFailure(result.error);
      return;
    }
    if (isDefined(result.refreshError)) {
      setBaseline(result.intervention);
      reset(getInterventionFormValues(result.intervention, settings));
      setHasRefreshError(true);
      setError('root', {
        message: t`The intervention was saved, but the list could not refresh. Try refreshing again.`,
      });
      return;
    }
    enqueueToast({
      variant: 'success',
      children: isDefined(baseline)
        ? t`Intervention updated`
        : t`Intervention created`,
    });
    closeSidePanel();
  };
  const handleReload = async () => {
    if (!isDefined(baseline) || isBusy) return;
    setIsPending(true);
    let latestIntervention: InterventionFieldsFragment | undefined;
    try {
      const result = await client.query({
        query: GetInterventionDocument,
        variables: { id: baseline.id },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        context: SKIP_ERROR_TOAST_CONTEXT,
      });
      if (!isDefined(result.data?.intervention)) {
        handleFailure(result.error, true);
        return;
      }
      latestIntervention = result.data.intervention;
    } catch (error) {
      handleFailure(error, true);
      return;
    } finally {
      setIsPending(false);
    }
    const nextSettings = {
      timeZone: workspace.timeZone,
      workingDays: workspace.workingDays,
      halfDaySlots: workspace.halfDaySlots,
    };
    setSettings(nextSettings);
    setBaseline(latestIntervention);
    reset(getInterventionFormValues(latestIntervention, nextSettings));
    setIsConflict(false);
  };
  const handleConfirm = async () => {
    if (
      !isDefined(baseline) ||
      isBusy ||
      isReadOnly ||
      isConflict ||
      hasRefreshError ||
      confirmationAction === null
    )
      return;
    setIsPending(true);
    const result =
      confirmationAction === 'cancel'
        ? await cancelIntervention(baseline.id, baseline.version)
        : await deleteIntervention(baseline.id, baseline.version);
    setIsPending(false);
    setConfirmationAction(null);
    if (!isDefined(result.intervention)) {
      handleFailure(result.error);
      return;
    }
    if (isDefined(result.refreshError)) {
      setBaseline(result.intervention);
      reset(getInterventionFormValues(result.intervention, settings));
      setHasRefreshError(true);
      setError('root', {
        message: t`The intervention was saved, but the list could not refresh. Try refreshing again.`,
      });
      return;
    }
    enqueueToast({
      variant: 'success',
      children:
        confirmationAction === 'cancel'
          ? t`Intervention cancelled`
          : t`Intervention deleted`,
    });
    if (confirmationAction === 'delete') {
      client.cache.evict({ id: client.cache.identify(result.intervention) });
      client.cache.gc();
    }
    closeSidePanel();
  };
  const handleRetryRefresh = async () => {
    if (isBusy) return;
    setIsPending(true);
    try {
      await client.refetchQueries({
        include: [GetInterventionsDocument, GetPlanningBoardDocument],
      });
      setHasRefreshError(false);
      closeSidePanel();
    } catch (error) {
      handleFailure(error);
    } finally {
      setIsPending(false);
    }
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (isSubmitShortcut(event)) {
      event.preventDefault();
      if (!isBusy && !isReadOnly && !isConflict && !hasRefreshError)
        event.currentTarget.requestSubmit();
    }
  };
  return (
    <StyledForm
      noValidate
      onSubmit={handleSubmit(handleSave)}
      onKeyDown={handleKeyDown}
    >
      {isReadOnly && (
        <StyledHint>{t`You can view interventions but not change them.`}</StyledHint>
      )}
      <SettingsTextField
        control={control}
        name="title"
        label={t`Title`}
        readOnly={isDisabled}
      />
      <Controller
        control={control}
        name="interventionTypeId"
        render={({ field }) => (
          <ReferenceSelect
            label={t`Intervention type`}
            value={field.value}
            onChange={field.onChange}
            disabled={isDisabled}
            options={[
              { value: '', label: t`Choose an intervention type` },
              ...availableTypes.map((interventionType) => ({
                value: interventionType.id,
                label: interventionType.name,
              })),
            ]}
          />
        )}
      />
      {errors.interventionTypeId && (
        <p role="alert">{errors.interventionTypeId.message}</p>
      )}
      <SettingsTextField
        control={control}
        name="clientName"
        label={t`Client`}
        readOnly={isDisabled}
      />
      <SettingsTextField
        control={control}
        name="clientPhone"
        label={t`Client phone`}
        autoComplete="tel"
        readOnly={isDisabled}
      />
      <SettingsTextField
        control={control}
        name="address"
        label={t`Address`}
        readOnly={isDisabled}
      />
      <Controller
        control={control}
        name="priority"
        render={({ field }) => (
          <ReferenceSelect
            label={t`Priority`}
            value={field.value}
            onChange={field.onChange}
            disabled={isDisabled}
            options={[
              { value: 'low', label: t`Low` },
              { value: 'normal', label: t`Normal` },
              { value: 'high', label: t`High` },
              { value: 'urgent', label: t`Urgent` },
            ]}
          />
        )}
      />
      <Controller
        control={control}
        name="estimatedDurationMinutes"
        render={({ field, fieldState }) => (
          <Field.Root invalid={isDefined(fieldState.error)}>
            <Field.Label>{t`Estimated duration (minutes)`}</Field.Label>
            <Input
              type="number"
              min={1}
              step={1}
              ref={field.ref}
              name={field.name}
              value={field.value === null ? '' : String(field.value)}
              readOnly={isDisabled}
              onValueChange={(duration) =>
                field.onChange(duration === '' ? null : Number(duration))
              }
              onBlur={field.onBlur}
            />
            <Field.Error match={isDefined(fieldState.error)}>
              {fieldState.error?.message}
            </Field.Error>
          </Field.Root>
        )}
      />
      <StyledHint>
        {t`Dates use the workspace time zone:`} {settings.timeZone}
      </StyledHint>
      <InterventionScheduleFields
        control={control}
        halfDaySlots={settings.halfDaySlots}
        disabled={isDisabled}
        onScheduleChange={handleScheduleChange}
      />
      {isDefined(baseline?.startsAt) && (
        <StyledHint>
          {t`Saved schedule:`}{' '}
          {formatInterventionTimestamp(
            baseline.startsAt,
            i18n.locale,
            settings.timeZone,
          )}{' '}
          –{' '}
          {isDefined(baseline.endsAt) &&
            formatInterventionTimestamp(
              baseline.endsAt,
              i18n.locale,
              settings.timeZone,
            )}
        </StyledHint>
      )}
      <Controller
        control={control}
        name="status"
        render={({ field }) => (
          <ReferenceSelect
            label={t`Status`}
            value={field.value}
            onChange={field.onChange}
            disabled={isDisabled || status === 'cancelled'}
            options={statusOptions}
          />
        )}
      />
      {isLoadingChoices && <p role="status">{t`Loading choices…`}</p>}
      {isDefined(referenceError) && (
        <div role="alert">
          <p>{t`Couldn't load reference data`}</p>
          <Button
            type="button"
            onClick={() => {
              referenceData.refetch().catch(() => undefined);
              interventionTypes.refetch().catch(() => undefined);
            }}
          >{t`Try again`}</Button>
        </div>
      )}
      {!isLoadingChoices && availableTypes.length === 0 && (
        <StyledHint>{t`No active intervention types. Add one in settings before creating an intervention.`}</StyledHint>
      )}
      <InterventionAssignmentPicker
        collaborators={[...availableCollaborators, ...existingCollaborators]}
        teams={referenceData.teams}
        skills={referenceData.skills}
        collaboratorIds={collaboratorIds}
        skillIds={skillIds}
        disabled={isDisabled}
        onCollaboratorsChange={(ids) => setValue('collaboratorIds', ids)}
        onSkillsChange={(ids) => setValue('skillIds', ids)}
      />
      {baseline?.assignments.map((assignment) => (
        <StyledHint key={assignment.id}>
          {assignment.collaborator?.fullName ?? assignment.collaboratorId}:{' '}
          {isDefined(assignment.effectiveStartsAt)
            ? formatInterventionTimestamp(
                assignment.effectiveStartsAt,
                i18n.locale,
                settings.timeZone,
              )
            : t`To plan`}{' '}
          –{' '}
          {isDefined(assignment.effectiveEndsAt)
            ? formatInterventionTimestamp(
                assignment.effectiveEndsAt,
                i18n.locale,
                settings.timeZone,
              )
            : t`To plan`}
        </StyledHint>
      ))}
      <Controller
        control={control}
        name="notes"
        render={({ field, fieldState }) => (
          <Field.Root invalid={isDefined(fieldState.error)}>
            <Field.Label>{t`Instructions`}</Field.Label>
            <Textarea
              ref={field.ref}
              name={field.name}
              value={field.value}
              readOnly={isDisabled}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              rows={3}
            />
            <Field.Error match={isDefined(fieldState.error)}>
              {fieldState.error?.message}
            </Field.Error>
          </Field.Root>
        )}
      />
      {isDefined(errors.root) && (
        <div role="alert">
          <p>{errors.root.message}</p>
          {hasRefreshError && (
            <Button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void handleRetryRefresh();
              }}
            >{t`Try again`}</Button>
          )}
          {isConflict && (
            <Button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void handleReload();
              }}
            >{t`Reload`}</Button>
          )}
        </div>
      )}
      {!isReadOnly && (
        <StyledActions>
          <Button
            type="submit"
            variant="solid"
            color="accent"
            loading={isBusy}
            disabled={
              isBusy ||
              isConflict ||
              hasRefreshError ||
              isLoadingChoices ||
              isDefined(referenceError)
            }
          >
            {isDefined(baseline) ? t`Save` : t`Create intervention`}
          </Button>
          <Button
            type="button"
            disabled={isBusy}
            onClick={closeSidePanel}
          >{t`Cancel`}</Button>
          {isDefined(baseline) && (
            <>
              <Button
                type="button"
                disabled={
                  isBusy ||
                  isConflict ||
                  hasRefreshError ||
                  status === 'cancelled'
                }
                onClick={() => setConfirmationAction('cancel')}
              >{t`Cancel intervention`}</Button>
              <Button
                type="button"
                color="danger"
                disabled={isBusy || isConflict || hasRefreshError}
                onClick={() => setConfirmationAction('delete')}
              >{t`Delete intervention`}</Button>
            </>
          )}
        </StyledActions>
      )}
      <InterventionConfirmation
        action={confirmationAction}
        isPending={isPending}
        onClose={() => setConfirmationAction(null)}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    </StyledForm>
  );
};
